import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiClock, FiUser, FiPhone, FiChevronDown, FiCheck, FiAlertTriangle } from 'react-icons/fi';

const ASSIGNABLE_USERS = [
  { id: '693a611bee1166397a0e4ec1', label: 'Admin 1' },
  { id: '693a611cee1166397a0e4ec2', label: 'Admin 2' },
];

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const ms = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  const diff = Math.ceil((d - now) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff < 0) return 'Overdue';
  return `${diff}d`;
}

export default function NotificationItem({
  item,
  type,
  isNew,
  onAssign,
  onClose,
  onOpen,
  accentColor = 'gray',
  assignableUsers,
}) {
  const [showAssign, setShowAssign] = useState(false);
  const users = assignableUsers || ASSIGNABLE_USERS;

  const isBlinking = item.urgencyColor === 'blinking-red';
  const isClosed = item.status === 'completed' || item.status === 'cancelled' || item.closed === true;
  const canManage = Boolean(item.ticketId || item.ticketTypeId || type === 'staleEscalated' || type === 'driverSupport' || type === 'driverStall');

  const accentMap = {
    coral: 'border-l-red-500',
    amber: 'border-l-amber-500',
    blue: 'border-l-blue-500',
    violet: 'border-l-violet-500',
    emerald: 'border-l-emerald-500',
    red: 'border-l-red-600',
    gray: 'border-l-gray-400',
  };

  const handleAssign = (userId) => {
    onAssign?.(item, userId);
    setShowAssign(false);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.97 }}
      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={`
        relative border-l-[3px] ${accentMap[accentColor] || accentMap.gray}
        py-2.5 pl-3 pr-2
        transition-colors duration-200
        ${isBlinking ? 'animate-blink-red' : ''}
        ${isNew ? 'bg-white/80 dark:bg-white/[0.04]' : ''}
        ${isClosed ? 'opacity-50' : ''}
        hover:bg-gray-50 dark:hover:bg-white/[0.03]
        cursor-pointer group
      `}
      onClick={() => onOpen?.(item)}
    >
      {isBlinking && (
        <div className="absolute inset-0 rounded-r bg-red-500/[0.06] animate-pulse pointer-events-none" />
      )}

      <div className="flex items-start justify-between gap-2 relative z-10">
        <div className="min-w-0 flex-1">
          {/* Title row */}
          <div className="flex items-center gap-1.5">
            {isBlinking && <FiAlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 animate-pulse" />}
            <span className={`text-[13px] font-semibold leading-tight truncate ${isBlinking ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
              {item.displayMessage || item.title || item.name || `#${item.uniqueId || item._id?.slice(-6)}`}
            </span>
          </div>

          {/* Subtitle */}
          <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 truncate">
            {type === 'driverless' && (
              <>
                {item.property?.name || item.property?.address?.line1 || item.storeLocation || 'Order'}
                {item.inStockDate && (
                  <span className={`ml-1.5 font-semibold ${daysUntil(item.inStockDate) === 'Today' || daysUntil(item.inStockDate) === 'Overdue' ? 'text-red-500' : 'text-amber-600 dark:text-amber-400'}`}>
                    {daysUntil(item.inStockDate)}
                  </span>
                )}
              </>
            )}
            {type === 'inquiry' && (
              <>{item.email} · {item.type || 'Other'}</>
            )}
            {type === 'serviceRequest' && (
              <>{item.servicesRequested?.slice(0, 2).join(', ') || 'Service'} · {item.email}</>
            )}
            {type === 'staleEscalated' && (
              <>{item.name} · P{item.priority?.replace('P', '')}</>
            )}
            {type === 'driverSupport' && (
              <>{item.name} · waiting {timeAgo(item.createdAt)}</>
            )}
            {type === 'noStoreOrder' && (
              <>No store order · {item.property?.name || item.propertyID?.name || item.storeLocation}</>
            )}
            {type === 'missingEntry' && (
              <>Entry instructions needed · {item.property?.name || item.propertyID?.name || item.storeLocation}</>
            )}
            {type === 'missingDelivery' && (
              <>Delivery time needed · {item.property?.name || item.propertyID?.name || item.storeLocation}</>
            )}
            {type === 'driverStall' && (
              <>
                <span className="flex items-center gap-1">
                  <FiUser className="w-3 h-3" />
                  {item.driverName || 'Contact Gig/Taskr'}
                  {item.driverPhone && (
                    <span className="flex items-center gap-0.5 text-red-500">
                      <FiPhone className="w-3 h-3" />
                      {item.driverPhone}
                    </span>
                  )}
                </span>
              </>
            )}
          </div>

          {/* Meta row */}
          <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-400 dark:text-gray-500">
            <span className="flex items-center gap-0.5">
              <FiClock className="w-3 h-3" />
              {timeAgo(item.createdAt || item.updatedAt)}
            </span>
            {item.assignedToName && (
              <span className="flex items-center gap-0.5">
                <FiUser className="w-3 h-3" />
                {item.assignedToName}
              </span>
            )}
            {item.replied === true && type === 'inquiry' && (
              <span className="text-emerald-500 font-medium">Replied</span>
            )}
          </div>
        </div>

        {/* Right side: status + actions */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {/* Status pill */}
          <span className={`
            inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider
            ${isClosed
              ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
              : isBlinking
                ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
            }
          `}>
            {isClosed ? 'Closed' : 'Open'}
          </span>

          {/* Action buttons */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {!isClosed && canManage && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAssign(!showAssign); }}
                  className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Assign"
                >
                  <FiUser className="w-3 h-3" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onClose?.(item); }}
                  className="p-1 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-gray-400 hover:text-emerald-600"
                  title="Close"
                >
                  <FiCheck className="w-3 h-3" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Assign dropdown */}
      {showAssign && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute right-2 top-full mt-1 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {users.map((u) => (
            <button
              key={u.id}
              onClick={() => handleAssign(u.id)}
              className="block w-full text-left px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {u.label}
            </button>
          ))}
        </motion.div>
      )}
    </motion.div>
  );
}
